import type {
  IApplicationVersion,
  IApplicationVersionForListing,
  IVersionComment,
  IVersionPayload,
} from '~/types/application-version'
import type { DeviceType } from '~/types/device-type'
import type { IAppVersion } from '~/types/version'

import config from '~/config'
import { paramsSerializer } from '~/utils/api'
import { request } from '~/utils/request'

/**
 * @deprecated use getApplicationVersions instead
 */
export const getAppVersions = (appId?: string): Promise<IAppVersion[]> => {
  return request({
    url: `${config.VERSIONS_ADMIN}/application/${appId}/versions`,
  })
}

/**
 * https://store-dev-europe-west1-molccirxma-ew.a.run.app/#get-/application-versions/device-type/-deviceType-/external-id/-externalID-
 * Get the application versions by store ID & device type
 * @param externalId The store ID of the application
 * @param deviceType The device type of the application
 * @returns A list of application versions
 */
export const getApplicationVersions = ({
  deviceType,
  externalId,
  ...queryParams
}: {
  deviceType: DeviceType
  externalId: string
} & IPaginatedQueryParams): Promise<IPaginatedResponse<IApplicationVersionForListing>> => {
  return request({
    url: `${
      config.STORE_API
    }/application-versions/device-type/${deviceType}/external-id/${externalId}?${paramsSerializer(queryParams)}`,
  })
}

interface IGetApplicationVersionQueryParams {
  country?: string
  lang?: string
}

export interface IGetApplicationVersionCommentsQueryParams {
  limit?: number
  page?: number
  parentsCommentId?: string
}

/**
 * @deprecated use getApplicationVersion instead
 */
export const getAppDetailsVersionPerId = ({
  appId,
  versionId,
}: {
  appId: string
  versionId: string
}): Promise<IAppVersion> => {
  return request({
    url: `${config.VERSIONS_API}/application/${appId}/version/${versionId}/details`,
  })
}

/**
 * https://store-dev-europe-west1-molccirxma-ew.a.run.app/#get-/application-versions/device-type/-deviceType-/external-id/-externalID-/semantic-version/-semanticVersion-
 * Get an application version by device type and external ID
 * @returns IApplicationVersion The application version
 */
export const getApplicationVersion = ({
  deviceType,
  externalId,
  semanticVersion,
  ...queryParams
}: {
  deviceType: DeviceType
  externalId: string
  semanticVersion: string
} & IGetApplicationVersionQueryParams): Promise<IApplicationVersion> => {
  const params = (Object.keys(queryParams) as (keyof IGetApplicationVersionQueryParams)[]).map(
    (key) => `${key}=${queryParams[key]}`
  )
  const endpointSuffix = params.length > 0 ? `?${params.join('&')}` : ''

  return request({
    url: `${config.STORE_API}/application-versions/device-type/${deviceType}/external-id/${externalId}/semantic-version/${semanticVersion}${endpointSuffix}`,
  })
}

/** @doc https://store-dev-europe-west1-molccirxma-ew.a.run.app/#get-/application-versions/device-type/-deviceType-/external-id/-externalID-/semantic-version/-semanticVersion-/comments */
export const getApplicationVersionComments = ({
  deviceType,
  externalId,
  semanticVersion,
  ...queryParams
}: IVersionPayload & IGetApplicationVersionCommentsQueryParams): Promise<IPaginatedResponse<IVersionComment>> => {
  return request({
    params: {
      ...queryParams,
    },
    url: `${config.STORE_API}/application-versions/device-type/${deviceType}/external-id/${externalId}/semantic-version/${semanticVersion}/comments`,
  })
}

/** @doc https://store-dev-europe-west1-molccirxma-ew.a.run.app/#post-/application-versions/device-type/-deviceType-/external-id/-externalID-/semantic-version/-semanticVersion-/changelog */
export const setAppVersionNote = ({
  content,
  deviceType,
  externalId,
  fileIds,
  semanticVersion,
}: {
  content: string
  fileIds: string[]
} & IVersionPayload): Promise<void> => {
  return request({
    data: { content, file_ids: fileIds },
    method: 'POST',
    url: `${config.STORE_API}/application-versions/device-type/${deviceType}/external-id/${externalId}/semantic-version/${semanticVersion}/changelog`,
  })
}

/** @doc https://store-dev-europe-west1-molccirxma-ew.a.run.app/#post-/application-versions/device-type/-deviceType-/external-id/-externalID-/semantic-version/-semanticVersion-/comments */
export const setAppVersionComments = ({
  content,
  deviceType,
  externalId,
  fileIds,
  parentCommentId,
  semanticVersion,
}: {
  content: string
  fileIds: string[]
  parentCommentId?: string
} & IVersionPayload): Promise<void> => {
  return request({
    data: { content, file_ids: fileIds, parent_comment_id: parentCommentId },
    method: 'POST',
    url: `${config.STORE_API}/application-versions/device-type/${deviceType}/external-id/${externalId}/semantic-version/${semanticVersion}/comments`,
  })
}

/** @doc https://store-dev-europe-west1-molccirxma-ew.a.run.app/#delete-/application-versions/comments/-commentID- */
export const deleteAppVersionComment = (commentID: string): Promise<void> => {
  return request({
    method: 'DELETE',
    url: `${config.STORE_API}/application-versions/comments/${commentID}`,
  })
}
